// src/http.ts
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ElMessage } from 'element-plus'; // 假设使用 Element Plus 作为 UI 框架
import router from '@/router'; // 导入路由实例

// 定义响应数据的接口
interface BaseResponse<T = any> {
  code: number;
  data: T;
  message: string;
}

// 添加防抖相关的配置接口
interface DebounceConfig {
  debounce?: boolean;
  debounceTime?: number;
}

// 创建实例
const http: AxiosInstance = axios.create({
  baseURL: process.env.NODE_ENV === 'production'
    ? `${process.env.VUE_APP_API_BASE_URL}/jing_chat_api/api/v1`
    : '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 添加请求标识管理
const pendingMap = new Map();

// 防抖Map
const debounceMap = new Map();

// 生成请求标识
const generateReqKey = (config: AxiosRequestConfig & DebounceConfig) => {
  const { method, url, params, data } = config;
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&');
};

// 添加防抖处理函数
const handleDebounce = (config: AxiosRequestConfig & DebounceConfig) => {
  if (!config.debounce) return;

  const requestKey = generateReqKey(config);
  const debounceTime = config.debounceTime || 1000; // 默认1秒

  if (debounceMap.has(requestKey)) {
    const prevTimer = debounceMap.get(requestKey);
    clearTimeout(prevTimer);
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      debounceMap.delete(requestKey);
      resolve(true);
    }, debounceTime);
    debounceMap.set(requestKey, timer);
  });
};

// 添加请求标识
const addPending = (config: AxiosRequestConfig) => {
  const requestKey = generateReqKey(config);
  if (!pendingMap.has(requestKey)) {
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingMap.set(requestKey, controller);
  } else {
    // 如果存在重复请求，取消新的请求，但不抛出错误
    const controller = pendingMap.get(requestKey);
    controller.abort();
    return false;
  }
  return true;
};

// 移除请求标识
const removePending = (config: AxiosRequestConfig) => {
  const requestKey = generateReqKey(config);
  if (pendingMap.has(requestKey)) {
    const controller = pendingMap.get(requestKey);
    controller.abort();
    pendingMap.delete(requestKey);
  }
};

// 修改请求拦截器
http.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    const internalConfig = config as InternalAxiosRequestConfig<any>;

    // 处理防抖
    if ((config as DebounceConfig).debounce) {
      try {
        await handleDebounce(config as AxiosRequestConfig & DebounceConfig);
      } catch (error) {
        return Promise.reject(error);
      }
    }

    // 在添加新请求之前，先移除之前的同名请求
    removePending(config);
    // 添加新的请求
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingMap.set(generateReqKey(config), controller);

    // 从 authData 中获取 token
    const authData = localStorage.getItem('authData');
    let token = null;
    if (authData) {
      try {
        const parsedAuthData = JSON.parse(authData);
        token = parsedAuthData.token;
      } catch (error) {
        console.error('解析 authData 失败:', error);
      }
    }

    if (token) {
      internalConfig.headers.Authorization = token
    }

    return internalConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
http.interceptors.response.use(
  (response) => {
    // 请求完成后，移除请求标识
    removePending(response.config);
    return response.data;
  },
  (error) => {
    // 如果是重复请求或被取消的请求，静默处理
    if (axios.isCancel(error) || error.isCancel) {
      return new Promise(() => { }); // 返回永远pending的promise，静默处理
    }

    // 请求失败也要移除请求标识
    error.config && removePending(error.config);

    let message = '请求失败';

    if (error.response) {
      const { status } = error.response;

      // HTTP 状态码处理
      switch (status) {
        case 400:
          message = '请求参数错误';
          break;
        case 401:
          message = '未授权，请登录';
          localStorage.removeItem('authData');
          router.push('/login');
          break;
        case 403:
          message = '没有权限访问该资源';
          if (error.response.data?.message?.includes('token')) {
            localStorage.removeItem('authData');
            router.push('/login');
          }
          break;
        case 404:
          message = '请求地址不存在';
          break;
        case 500:
          message = '服务器内部错误';
          break;
        case 503:
          message = '服务不可用';
          break;
        default:
          message = `请求失败(${status})`;
      }
    } else if (error.request) {
      message = '网络请求超时';
    }

    ElMessage.error(message);
    return Promise.reject(error);
  }
);

export default http;